import BigNumber from 'bignumber.js'
import { AutoColumn } from 'components/Layout/Column'
import { RowFixed } from 'components/Layout/Row'
import { CurrencyLogo } from 'components/Logo'
import { TagComponent, tagTypes } from 'components/Tags'
import { TokenPairImage } from 'components/TokenImage'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { usePair } from 'hooks/usePairs'
import React, { useMemo } from 'react'
import { getExplorerLink } from 'utils'
import { getAddress } from 'utils/addressHelpers'
import { getBalanceAmount } from 'utils/formatBalance'
import { unwrappedToken, wrappedCurrency } from 'utils/wrappedCurrency'
import { TokenAmount } from 'vvs-sdk'
import { Flex, Text, CardBody, Button, Link } from 'vvs-uikit'
import { FixedHeightRow, FixedHeightRowEnd, StyledCard } from '../styles'
import { LiquidityCardProps } from '../types'

const HistoryCard: React.FC<LiquidityCardProps> = (props) => {
  const { t } = useTranslation()

  const { redemption, pair, farms } = props
  const { id, liquidity, pair: redemPair, amount0, amount1, timestamp, token0AmountRule, token1AmountRule } = redemption

  const { chainId } = useActiveWeb3React()

  const selectedFarm = farms.find(f => getAddress(f.lpAddresses).toLowerCase() === redemPair.id.toLowerCase())
  const [, selectedPair] = usePair(unwrappedToken(selectedFarm.quoteToken), unwrappedToken(selectedFarm.token))
  const [currency0, currency1] = [wrappedCurrency(selectedPair.token0, chainId), wrappedCurrency(selectedPair.token1, chainId)]
  const [token0, token1] = useMemo(
    () => [wrappedCurrency(currency0, chainId), wrappedCurrency(currency1, chainId)],
    [currency0, currency1, chainId],
  )

  if (!pair) {
    return <Text>Loading...</Text>
  }

  return <StyledCard>
    <CardBody p={0}>
      <AutoColumn gap="8px" style={{ padding: '16px' }}>

        <Flex marginBottom="15px" justifyContent="space-between">
          <TagComponent title={t('Removed')} variant={tagTypes.FAILURE} />
        </Flex>

        <Flex justifyContent="space-between" alignItems="center">
          <TokenPairImage
            variant="inverted"
            primaryToken={pair.token0}
            secondaryToken={pair.token1}
            width={32}
            height={32}
          />
          <Text fontSize="20px" bold>
            {`${currency0.symbol}/${currency1.symbol}`}
          </Text>
        </Flex>

        <Flex justifyContent="flex-start" role="button" my={3}>
          <Text fontSize="20px" bold>
            {getBalanceAmount(new BigNumber(liquidity)).toString()}
          </Text>
        </Flex>

        <FixedHeightRow>
          <Text mb={1} textTransform="uppercase">{t('Received from smart removal')}</Text>
        </FixedHeightRow>
        <FixedHeightRow>
          <RowFixed>
            <CurrencyLogo size="20px" currency={currency0} />
            <Text color="textSubtle" ml="4px">
              {currency0.symbol}
            </Text>
          </RowFixed>
          {amount0 ? (
            <RowFixed>
              <Text small ml="6px">{new TokenAmount(token0, amount0).toSignificant(6)}</Text>
            </RowFixed>
          ) : (
            '-'
          )}
        </FixedHeightRow>
        <FixedHeightRowEnd>
          <Text>
            {token0AmountRule
              ? <Text small color="textSubtle">{`(target: ${new TokenAmount(token0, token0AmountRule).toSignificant(6)})`}</Text>
              : '(target: -)'
            }
          </Text>
        </FixedHeightRowEnd>

        <FixedHeightRow>
          <RowFixed>
            <CurrencyLogo size="20px" currency={currency1} />
            <Text color="textSubtle" ml="4px">
              {currency1.symbol}
            </Text>
          </RowFixed>
          {amount1 ? (
            <RowFixed>
              <Text small ml="6px">{new TokenAmount(token1, amount1).toSignificant(6)}</Text>
            </RowFixed>
          ) : (
            '-'
          )}
        </FixedHeightRow>
        <FixedHeightRowEnd>
          <Text>
            {token1AmountRule
              ? <Text small color="textSubtle">{`(target: ${new TokenAmount(token1, token1AmountRule).toSignificant(6)})`}</Text>
              : '(target: -)'
            }
          </Text>
        </FixedHeightRowEnd>

        <FixedHeightRow mt={2}>
          <Text small color="textSubtle">{t('Removed On')}</Text>
          <Text>
            {new Date(parseInt(timestamp) * 1000).toLocaleString("en-US", { year: 'numeric', month: 'short', day: 'numeric', hourCycle: 'h24', hour: '2-digit', minute: '2-digit' })}
          </Text>
        </FixedHeightRow>

        <FixedHeightRow mb={2}>
          <Text small color="textSubtle">{t('Transaction')}</Text>
          <Button as={Link} variant="text" p={0} href={`${getExplorerLink(id.split('-')[0], 'transaction', chainId)}`} target="_blank">
            <Text small bold color="#1199FA">{t('View on Explorer')}</Text>
          </Button>
        </FixedHeightRow>
      </AutoColumn>
    </CardBody>
  </StyledCard>
}

export default HistoryCard